import { useState } from "react";
import { useValidationState } from "../hooks/useValidationState";
import { STEP_CONFIG, STATUS_CONFIG } from "../domain/types";
import type { ValidationStep, ValidationStatus } from "../domain/types";

interface Props {
  fundId: number;
  quarter: string;
}

const STEPS: ValidationStep[] = ["step_1", "step_2", "step_3"];

interface ActionState {
  step: ValidationStep;
  status: ValidationStatus;
  comment: string;
}

const STEP_NUMBER: Record<ValidationStep, string> = {
  step_1: "1",
  step_2: "2",
  step_3: "3",
};

export function ValidationPanel({ fundId, quarter }: Props) {
  const { events, isLoading, submitEvent, isSubmitting } = useValidationState(
    fundId,
    quarter,
  );
  const [action, setAction] = useState<ActionState | null>(null);

  const eventByStep = Object.fromEntries(
    events.map((e) => [e.step, e]),
  ) as Partial<Record<ValidationStep, (typeof events)[0]>>;

  function isUnlocked(step: ValidationStep): boolean {
    if (step === "step_1") return true;
    const step1Validated = eventByStep["step_1"]?.status === "validated";
    const step2Validated = eventByStep["step_2"]?.status === "validated";
    if (step === "step_2") return step1Validated;
    return step1Validated && step2Validated;
  }

  function startAction(step: ValidationStep, status: ValidationStatus) {
    setAction({ step, status, comment: "" });
  }

  function confirm() {
    if (!action) return;
    submitEvent(
      {
        fund_id: fundId,
        quarter,
        step: action.step,
        status: action.status,
        comment: action.comment || undefined,
      },
      { onSuccess: () => setAction(null) },
    );
  }

  if (isLoading) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="grid grid-cols-3 divide-x divide-slate-100">
        {STEPS.map((step) => {
          const cfg = STEP_CONFIG[step];
          const current = eventByStep[step];
          const statusKey: ValidationStatus = current?.status ?? "pending";
          const isActing = action?.step === step;
          const unlocked = isUnlocked(step);

          const isValidated = statusKey === "validated";
          const isRejected = statusKey === "rejected";

          return (
            <div
              key={step}
              className={`px-6 py-4 ${!unlocked ? "bg-slate-50/60" : "bg-white"}`}
            >
              {/* Header */}
              <div className="flex items-center gap-2 mb-3">
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                    isValidated
                      ? "bg-emerald-100 text-emerald-700"
                      : unlocked
                        ? "bg-blue-100 text-blue-600"
                        : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {isValidated ? "✓" : STEP_NUMBER[step]}
                </span>
                <span
                  className={`text-xs font-semibold ${
                    isValidated
                      ? "text-emerald-700"
                      : unlocked
                        ? "text-slate-700"
                        : "text-slate-400"
                  }`}
                >
                  {cfg.label}
                </span>
                {unlocked && (
                  <span
                    className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      isValidated
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                        : isRejected
                          ? "bg-red-50 text-red-600 ring-1 ring-red-200"
                          : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {STATUS_CONFIG[statusKey].label}
                  </span>
                )}
              </div>

              {/* Last event metadata */}
              {unlocked && current ? (
                <div className="mb-3 space-y-0.5 text-[11px] text-slate-400">
                  <p className="truncate">{current.user_email}</p>
                  <p>{new Date(current.created_at).toLocaleString()}</p>
                  {current.comment && (
                    <p className="mt-1 italic text-slate-500">
                      "{current.comment}"
                    </p>
                  )}
                </div>
              ) : !unlocked ? (
                <p className="mb-3 text-[11px] text-slate-400 italic">
                  Requires previous step validated
                </p>
              ) : (
                <p className="mb-3 text-[11px] text-slate-400 italic">
                  Not yet reviewed
                </p>
              )}

              {/* Actions */}
              {unlocked &&
                (isActing ? (
                  <div className="space-y-2">
                    <input
                      autoFocus
                      className="w-full rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs placeholder:text-slate-400 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
                      placeholder="Optional comment…"
                      value={action.comment}
                      onChange={(e) =>
                        setAction({ ...action, comment: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") confirm();
                        if (e.key === "Escape") setAction(null);
                      }}
                    />
                    <div className="flex gap-2">
                      <button
                        className={`flex-1 rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                          action.status === "validated"
                            ? "bg-emerald-600 text-white hover:bg-emerald-700"
                            : "bg-slate-700 text-white hover:bg-slate-800"
                        }`}
                        disabled={isSubmitting}
                        onClick={confirm}
                      >
                        {isSubmitting ? "…" : "Confirm"}
                      </button>
                      <button
                        className="rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px] font-medium text-slate-500 hover:bg-slate-50 transition-colors"
                        onClick={() => setAction(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    {statusKey !== "validated" && (
                      <button
                        className="rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                        onClick={() => startAction(step, "validated")}
                      >
                        Validate
                      </button>
                    )}
                    {statusKey !== "rejected" && (
                      <button
                        className="rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-600 transition-colors"
                        onClick={() => startAction(step, "rejected")}
                      >
                        Reject
                      </button>
                    )}
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
